import { useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { showToast } from '../../utils/toast';
import { authModule } from '../../api/modules/auth.module';
import { track, aliasOnSignup } from '../../analytics/track';
import { EVENTS } from '../../analytics/events';
import { SignupView } from './Signup.view';

/**
 * Sign up, the container. Registration, analytics, toasts, the redirect.
 *
 * Everything visual lives in `Signup.view.jsx`, which is pure, so the state
 * explorer can mount the real screen without any of this running.
 *
 * This page no longer emits a class name from `../Login/login.css` and no
 * longer imports it. The file itself has to stay for now: `LandingPage` and
 * `EmailVerification` still import it, which is exactly the coupling this
 * conversion exists to remove. It goes when the last of them is converted.
 *
 * The submit path below is unchanged from the pre-conversion component,
 * including its error handling. See the note on `result.error.errors`.
 */

export default function SignUp() {
  const { isAuthenticated, loading, error, message } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const source = new URLSearchParams(location.search).get('source') || 'signup_page';
    track(EVENTS.SIGNUP_STARTED, { source });
  }, [location.search]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/marketplace', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    const { confirmPassword, agreeToTerms, ...signupData } = values;

    try {
      // Use the authModule.register function instead of the signup function
      const result = await authModule.register({
        email: signupData.email,
        password: signupData.password,
        full_name: signupData.full_name,
        role: signupData.role,
      });

      if (result.success) {
        const user = result.user || {
          id: result.data?.user?.id || result.data?.id,
          email: signupData.email,
          full_name: signupData.full_name,
          role: signupData.role,
          signup_method: 'email',
        };
        aliasOnSignup(user);
        track(EVENTS.SIGNUP_COMPLETED, { method: 'email', role: signupData.role });
        showToast.success(result.message || 'Account created! Please verify your email.');
        // Redirect to email verification OTP page
        navigate(`/email-verification-otp?email=${encodeURIComponent(signupData.email)}`, {
          replace: true,
        });
      } else {
        // Preserved as-is. NOTE: this reads `result.error.errors` without
        // guarding either level, so a rejection that carries a plain string
        // `error` throws here and lands in the catch below as "An unexpected
        // error occurred". Flagged, not silently changed, because fixing it
        // changes which toast a user sees.
        showToast.error(
          result.error.errors.email ||
            result.error.errors.full_name ||
            result.error.message ||
            'Failed to create account.'
        );
        // Handle specific field errors if backend provides them
        if (result.message?.toLowerCase().includes('email') || result.error.errors?.email) {
          setFieldError('email', result.error.errors.email);
        } else if (result.message?.toLowerCase().includes('name')) {
          setFieldError('full_name', result.error.errors.full_name);
        }
      }
    } catch (submitError) {
      showToast.error('An unexpected error occurred. Please try again.');
      console.error('Signup error:', submitError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SignupView onSubmit={handleSubmit} loading={loading} error={error} message={message} />
  );
}